import { useEffect, useState } from 'react';

import { AUTH_AUDIENCE, AUTH_CONFIGURED, useAuthSession } from '@/auth';

import { HandbookAccessError, fetchHandbook } from './handbookClient';
import type { HandbookDocument } from './handbookTypes';

export type HandbookLoad =
  | { state: 'loading' }
  | { state: 'ready'; handbook: HandbookDocument }
  | { state: 'no-access' }
  | { state: 'failed'; message: string };

/** Asks the API for the handbook as the signed-in person; an unconfigured local build asks without a token. */
export function useHandbookDocument(isAuthenticated: boolean): HandbookLoad {
  const { getAccessTokenSilently } = useAuthSession();
  const [load, setLoad] = useState<HandbookLoad>({ state: 'loading' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (AUTH_CONFIGURED && !isAuthenticated) return undefined;
    let cancelled = false;
    const run = async () => {
      try {
        const token = AUTH_CONFIGURED
          ? await getAccessTokenSilently({ authorizationParams: AUTH_AUDIENCE ? { audience: AUTH_AUDIENCE } : undefined })
          : undefined;
        const handbook = await fetchHandbook(token);
        if (!cancelled) setLoad({ state: 'ready', handbook });
      } catch (error) {
        if (cancelled) return;
        if (error instanceof HandbookAccessError) setLoad({ state: 'no-access' });
        else setLoad({ state: 'failed', message: error instanceof Error ? error.message : String(error) });
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [attempt, getAccessTokenSilently, isAuthenticated]);

  useEffect(() => {
    if (load.state !== 'failed') return undefined;
    const retry = window.setTimeout(() => setAttempt((count) => count + 1), 5_000);
    return () => window.clearTimeout(retry);
  }, [load]);

  return load;
}
